import React, { useState, useEffect } from 'react'
import { decamelizeKeys } from 'humps'
import moment from 'moment-timezone'
import { ArrayParam, StringParam, withDefault } from 'use-query-params'
import { callApi, ENDPOINTS } from 'utils/api/callApi'
import { IRawEdiFile, IRawFilters } from 'apps/edi-tools/types'
import { useQueryParams } from 'utils/hooks'
import { Filters } from './Filters'
import { RawEdiFilesTable as Table } from './Table'

interface EdiFilesListResponse {
  rawFiles: [IRawEdiFile]
  nextPage: boolean
  numPages: number
}

const recordsPerPage = 12
const maxNumberOfPages = 100

export const FilesTableMapper = () => {
  const [ediFiles, setEdiFiles] = useState<IRawEdiFile[]>([])
  const [filters, setFilters] = useQueryParams<IRawFilters>({
    shipperId: withDefault(StringParam, ''),
    carrierId: withDefault(StringParam, ''),
    shipmentId: withDefault(StringParam, ''),
    fileTypes: withDefault(ArrayParam, []),
    createdBeforeDate: withDefault(StringParam, ''),
    createdAfterDate: withDefault(StringParam, ''),
  })
  const [page, setPage] = useState(1)
  const [numPages, setNumPages] = useState(1)
  const [loading, setLoading] = useState(false)
  const [loadingError, setLoadingError] = useState(false)

  const fetchDataFromApi = async () => {
    setLoading(true)
    setLoadingError(false)

    const {
      carrierId,
      createdAfterDate,
      createdBeforeDate,
      fileTypes,
      shipmentId,
      shipperId,
    } = filters

    const filterString = JSON.stringify(
      decamelizeKeys({
        ...(carrierId ? { carrierId } : {}),
        ...(shipperId ? { shipperId } : {}),
        ...(shipmentId ? { shipmentId } : {}),
        ...(fileTypes.length ? { ediMessageType: fileTypes } : {}),
        ...(createdBeforeDate ? { createdBefore: moment(createdBeforeDate).unix() } : {}),
        ...(createdAfterDate ? { createdAfter: moment(createdAfterDate).unix() } : {}),
      })
    )

    const queryParams = {
      filters: filterString,
      page_count: maxNumberOfPages,
      per_page: recordsPerPage,
      page: page - 1,
    }

    try {
      const res: EdiFilesListResponse = await callApi({
        endpoint: ENDPOINTS.ediFilesMapper,
        method: 'GET',
        params: queryParams,
      })
      setNumPages(res.numPages)
      setLoading(false)
      setEdiFiles(res.rawFiles)
    } catch (e) {
      setLoading(false)
      setLoadingError(true)
    }
  }

  const applyFilters = (updatedFilters: IRawFilters) => {
    if (filters !== updatedFilters) setPage(1)
    setFilters({ ...filters, ...updatedFilters })
  }

  const onPageChange = (pageNumber: number) => {
    setPage(pageNumber)
  }

  useEffect(() => {
    fetchDataFromApi()
  }, [page, filters])

  return (
    <div className='flex justify-center'>
      <div className='flex flex-grow-1'>
        <div className='flex flex-column col-lg-2'>
          <Filters filters={filters} applyFilters={applyFilters} />
        </div>
        <Table
          ediFiles={ediFiles}
          currentPage={page}
          numberOfPages={numPages}
          onPageChange={onPageChange}
          loading={loading}
          loadingError={loadingError}
        />
      </div>
    </div>
  )
}
